import { createContext, useContext } from 'react';

export const impactProducts = [
  'impact',
  'impact_unpaid',
  'impact_basic',
  'impact_advanced',
  'impact_premium',
] as const;
export type ImpactProduct = typeof impactProducts[number];

export const releaseDateKeysDates = [
  'releaseDates',
  'embargoEndDates',
] as const;

export const releaseDateKeysBoolean = [
  'isAfterReleaseDate',
  'isAfterEmbargoDate',
  'isAfterFreeEmbargoDate',
  'isAfterWurDetailsEmbargoDate',
] as const;

export type ReleaseDatesKeysDates = typeof releaseDateKeysDates[number];
export type ReleaseDatesKeysBoolean = typeof releaseDateKeysBoolean[number];

type IsAfterDate = Record<
  ReleaseDatesKeysDates | ReleaseDatesKeysBoolean,
  Record<string, boolean | Date>
>;

export type ImpactProductDates = Record<ImpactProduct, IsAfterDate>;

// To allow for type safety we create a 'default' object of all the impact product dates
// Which has all keys and structure of the expected impactReleaseDates object but empty values
// This way we can pass this as a default value to the provider context
export const defaultImpactReleaseDatesObject = Object.fromEntries(
  impactProducts.map((product) => [
    product,
    Object.fromEntries(
      [...releaseDateKeysDates, ...releaseDateKeysBoolean].map((key) => [
        key,
        {},
      ]),
    ),
  ]),
) as ImpactProductDates;

export type Peer = {
  name: string;
  id: string;
  institutionId: string;
  country: string;
  continent: string;
  logo: string;
};
export type SortItem = {
  label: string;
  value: string;
};
export interface PageQueryProps {
  querySelector: {};
  selectedYear: number;
  years: number[];
  releaseDates: { [key: string]: Date };
  embargoEndDates: { [key: string]: Date };
  isAfterReleaseDate: { [key: string]: Boolean };
  isAfterEmbargoDate: { [key: string]: Boolean };
  isAfterFreeEmbargoDate: { [key: string]: Boolean };
  isAfterWurDetailsEmbargoDate: { [key: string]: Boolean };
  impactReleaseDates: ImpactProductDates;
  selectedPeers: Peer[];
  selectedWurInterimPeers: Peer[];
  selectedGroupAnalysisPeers: Peer[];
  selectedQueryBuilderPeers: Peer[];
  peers: Peer[];
  wurInterimPeers: Peer[];
  selectedGroup?: string | null;
  groups: string[];
  continents: string[];
  countries: string[];
  downloadInProgress: boolean;
  sortItems: SortItem[];
  sortBy?: string;
  sortDirection?: string;
  scoresRegion: string;
}
interface PageContextProps {
  query: PageQueryProps;
  setSelectedYear: (year: number) => void;
  setQuerySelector: (query: object) => void;
  setSelectedPeers: (peers: Peer[] | ((peers: Peer[]) => Peer[])) => void;
  setSelectedWurInterimPeers: (
    peers: Peer[] | ((peers: Peer[]) => Peer[]),
  ) => void;
  setSelectedGroupAnalysisPeers: (
    peers: Peer[] | ((peers: Peer[]) => Peer[]),
  ) => void;
  setSelectedQueryBuilderPeers: (
    peers: Peer[] | ((peers: Peer[]) => Peer[]),
  ) => void;
  setSelectedGroup: (groupName: string) => void;
  setDownloadInProgress: (loading: boolean) => void;
  setSortItems: (items: SortItem[]) => void;
  setSortBy: (value: string) => void;
  setSortDirection: (value: string) => void;
  setScoresRegion: (value: string) => void;
}

export const defaultContextValue = {
  query: {
    querySelector: {},
    selectedYear: new Date().getFullYear(),
    years: [],
    releaseDates: {},
    embargoEndDates: {},
    isAfterReleaseDate: {},
    isAfterEmbargoDate: {},
    isAfterFreeEmbargoDate: {},
    isAfterWurDetailsEmbargoDate: {},
    impactReleaseDates: defaultImpactReleaseDatesObject,
    embargoEndDate: '',
    selectedPeers: [],
    selectedWurInterimPeers: [],
    selectedGroupAnalysisPeers: [],
    selectedQueryBuilderPeers: [],
    peers: [],
    wurInterimPeers: [],
    selectedGroup: null,
    groups: [],
    continents: [],
    countries: [],
    downloadInProgress: false,
    sortItems: [],
    sortBy: undefined,
    sortDirection: undefined,
    scoresRegion: 'Worldwide',
  },
  setSelectedYear: () => {},
  setQuerySelector: () => {},
  setSelectedPeers: () => {},
  setSelectedWurInterimPeers: () => {},
  setSelectedGroupAnalysisPeers: () => {},
  setSelectedQueryBuilderPeers: () => {},
  setSelectedGroup: () => {},
  setDownloadInProgress: () => {},
  setSortItems: () => {},
  setSortBy: () => {},
  setSortDirection: () => {},
  setScoresRegion: () => {},
};

export const PageQueryContext = createContext<PageContextProps>(
  defaultContextValue,
);

export const usePageQuery = () => useContext(PageQueryContext);

export default PageQueryContext;
